import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  XCircle,
} from 'lucide-react';
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';

/**
 * TOASTS
 *
 * Built on sonner, which already handles stacking, timing, swipe-to-dismiss
 * and the screen-reader live region. What this adds is a semantic API, so a
 * success toast looks the same everywhere instead of each call site picking an
 * icon and a colour.
 *
 * Mount <Toaster /> once, near the root of the app.
 */

export interface ToastOptions {
  /** Second line, for detail that doesn't fit in the title. */
  description?: string;
  /** Milliseconds on screen. Errors default to staying until dismissed. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

const iconProps = { className: 'size-icon-md shrink-0' };

/**
 * Show a toast.
 *
 *   toast.success('Call completed');
 *   toast.error('Could not save', { description: 'Check your connection' });
 *   toast.promise(save(), { loading: 'Saving…', success: 'Saved', error: 'Failed' });
 */
export const toast = {
  success(message: string, options?: ToastOptions) {
    return sonnerToast.success(message, {
      icon: (
        <CheckCircle2 {...iconProps} className="size-icon-md text-success" />
      ),
      ...options,
    });
  },

  /**
   * Errors persist until dismissed by default. An error that vanishes after
   * four seconds is an error the user never read.
   */
  error(message: string, options?: ToastOptions) {
    return sonnerToast.error(message, {
      icon: (
        <XCircle {...iconProps} className="size-icon-md text-destructive" />
      ),
      duration: Infinity,
      ...options,
    });
  },

  warning(message: string, options?: ToastOptions) {
    return sonnerToast.warning(message, {
      icon: (
        <AlertTriangle {...iconProps} className="size-icon-md text-warning" />
      ),
      ...options,
    });
  },

  info(message: string, options?: ToastOptions) {
    return sonnerToast.info(message, {
      icon: <Info {...iconProps} className="size-icon-md text-info" />,
      ...options,
    });
  },

  loading(message: string, options?: ToastOptions) {
    return sonnerToast.loading(message, {
      icon: (
        <Loader2
          {...iconProps}
          className="size-icon-md animate-spin text-muted-foreground"
        />
      ),
      ...options,
    });
  },

  /** Ties a toast to a promise: loading, then success or error. */
  promise: sonnerToast.promise,

  dismiss: sonnerToast.dismiss,
};

/**
 * The card a toast is drawn on. Given per type rather than to every toast:
 * `toast.custom` has no type, and its content (a ToastCard) is already the
 * card, so a second one around it drew the welcome toast as a card in a card.
 */
const toastSurface =
  'rounded-lg border border-border bg-popover p-4 text-popover-foreground shadow-e3';

/**
 * Mount once near the root of the app.
 *
 * Styling comes from tokens via `toastOptions.classNames` rather than sonner's
 * own theme, so toasts match the rest of the product and follow dark mode.
 */
export function Toaster({
  position = 'bottom-right',
}: {
  position?: React.ComponentProps<typeof SonnerToaster>['position'];
}) {
  return (
    <SonnerToaster
      position={position}
      // sonner's built-in themes are opted out of so nothing bypasses tokens.
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: 'flex w-full items-start gap-3',
          success: toastSurface,
          error: toastSurface,
          warning: toastSurface,
          info: toastSurface,
          loading: toastSurface,
          title: 'font-sans text-body font-medium',
          description: 'font-sans text-body-sm text-muted-foreground',
          actionButton:
            'ml-auto rounded-md bg-primary px-2.5 py-1 font-sans text-body-sm font-medium text-primary-foreground',
          cancelButton:
            'ml-auto rounded-md bg-muted px-2.5 py-1 font-sans text-body-sm font-medium text-muted-foreground',
          closeButton: 'border-border bg-popover text-muted-foreground',
        },
      }}
    />
  );
}
